import { useId, useRef, useState } from 'react'

import { ArrowRight, Sparkle } from './icons'

// ============================================================================
// THE ASK BAR — the first thing on the page, and the page's whole argument.
//
// README §6: "Order of the page is the whole argument: ask bar first, clusters
// second, library third. The ask bar is a chat scoped to the saves." 247 saved
// posts are an archive; a corpus you can ask a question of is a library. So this
// is not a search field wearing a sparkle — it sits above the clusters, in the
// header band, at 22px, with the page's only filled dark button.
//
// AND IT STILL DOES NOT ANSWER — but the reason changed, so the words did too.
//
// The chat engine is LIVE (V4, apps/worker/src/routes/chat.ts) and it has an
// `interests` scope. What it does not have is anything to read: the retrieval
// layer every answer is built out of covers seven tables and `saves` is not one
// of them (apps/worker/src/lib/search.ts → SEARCHABLE, whose own comment says
// so), and the chat engine's `CITABLE_TABLES.interests` is therefore an empty
// list (apps/worker/src/lib/chat.ts). An Interests-scoped question today reaches
// the engine and comes back "nothing here to cite", every time.
//
// SO THIS STAYS UNSENT, and says the true reason rather than a version number
// that has already shipped. Wiring it to a scope that can only refuse would be
// worse than the notice: it would spend a model call to produce the same
// sentence, and it would make the ask bar look broken instead of unfinished.
// What unblocks it is indexing saves — one row in SEARCHABLE, one column in the
// migration, one entry in CITABLE_TABLES — not another release.
//
// The Connections screen settled how to render something that is designed and
// unbuilt: render it fully, keep it reachable by keyboard, and let it SAY what
// it is waiting for — never a control that silently does nothing. Here that
// means the bar is real (you can type in it, the suggestion chips fill it, it
// submits) and what it submits to is a dashed, plainly-worded notice.
// A greyed-out bar would have hidden the page's argument behind its own absence.
// ============================================================================

const SUGGESTIONS = ['Summarize my AI video saves', 'What did I save about memory?']

export function AskBar() {
  const inputId = useId()
  const input = useRef<HTMLInputElement>(null)
  const [asked, setAsked] = useState(false)

  // UNCONTROLLED, like the journal composer: React never owns this value, so the
  // browser's own undo stack survives a suggestion chip filling the field.
  function fill(text: string) {
    if (!input.current) return
    input.current.value = text
    input.current.focus()
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        setAsked(true)
      }}
      className="flex flex-none flex-col gap-3 rounded-context border border-line-2 bg-surface px-[18px] py-[15px] shadow-ask"
    >
      <div className="flex items-center gap-[13px]">
        <Sparkle size={21} className="flex-none text-chat" />
        <label htmlFor={inputId} className="sr-only">
          Ask about anything you’ve saved
        </label>
        <input
          id={inputId}
          ref={input}
          type="text"
          autoComplete="off"
          placeholder="Ask about anything you’ve saved…"
          className="min-w-0 flex-1 bg-transparent text-[22px] font-light leading-[1.2] tracking-[-0.01em] text-ink outline-none placeholder:text-microcopy"
        />
        <button
          type="submit"
          className="flex flex-none items-center gap-[7px] rounded-row bg-ink px-[15px] py-[9px] text-[12px] font-semibold leading-none text-surface transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-ink-2"
        >
          Ask
          <ArrowRight size={13} className="text-chat-lift" />
        </button>
      </div>

      <div className="flex items-center gap-2">
        {SUGGESTIONS.map((text) => (
          <button
            key={text}
            type="button"
            onClick={() => fill(text)}
            className="flex-none rounded-pill border border-dashed border-dash px-3 py-[6px] text-[11.5px] font-medium leading-none text-muted transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2 hover:text-ink-2"
          >
            {text}
          </button>
        ))}
        <span className="ml-auto flex-none text-[10.5px] leading-none text-faintest-2">
          answers cite the saves they came from
        </span>
      </div>

      {asked && <AskNotice />}
    </form>
  )
}

/**
 * What the bar submits to. The handoff's copy pattern, in order: name the
 * consequence, name what still works, offer exactly one action — and here there
 * is no action, for the same reason the rate-limited connector gets no button.
 * Pressing something would not put the saves in the index.
 */
function AskNotice() {
  return (
    <div
      role="status"
      className="flex items-start gap-[10px] rounded-row border border-dashed border-dash bg-surface-2 px-[13px] py-[11px]"
    >
      <span className="mt-[1px] flex-none rounded-pill bg-fill-2 px-[7px] py-[2px] text-[9.5px] font-semibold leading-[1.6] tracking-[0.04em] text-muted">
        NOT ANSWERED
      </span>
      <p className="text-[11.5px] leading-[1.45] text-ink-3 text-pretty">
        Chat can answer — it just cannot read this. The index it retrieves from holds your
        journal, courses, tasks and learning items; your saves are not in it, so an answer here
        would have nothing to cite. Chat is live for everything that is indexed, your question
        stays in the bar, and the clusters below are the whole library until saves are indexed
        too.
      </p>
    </div>
  )
}
